import type { MutableRefObject } from 'react';
import { useEffect } from 'react';

type RefOrElement<T> = MutableRefObject<T> | T;

const isMutableRefObject = <T>(
  refOrElement: MutableRefObject<T> | T
): refOrElement is MutableRefObject<T> =>
  typeof (refOrElement as MutableRefObject<T>)?.current !== 'undefined';

const isTargetNode = <T extends HTMLElement | null>(
  element: RefOrElement<T>,
  target: HTMLElement
) => {
  const node = isMutableRefObject<T>(element) ? element.current : element;
  return !node || node.isSameNode(target) || node.contains(target);
};

// https://usehooks.com/useOnClickOutside/
const useOnClickOutside = <T extends HTMLElement | null>(
  refOrElement: RefOrElement<T>[] | RefOrElement<T>,
  // Any as event type here because React events and DOM event types used and it's not compatible.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler: (event: any) => void
) => {
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listener = (event: any) => {
      // Some components are using ref as a state (react-popper). To make this hook
      // more flexible we can check for the type before we run the handler.
      if (Array.isArray(refOrElement)) {
        if (refOrElement.some((r) => isTargetNode(r, event.target))) return;
      } else if (isTargetNode(refOrElement, event.target)) return;

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [refOrElement, handler]);
};

export default useOnClickOutside;
